import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, Feather, Loader2, Lock, LockOpen } from "lucide-react";
import {
  Container, PageLayout, Headline, MutedText, SeedTag, Card, Pill, NewBadge, ScheduledBadge, HiddenPackBadge,
} from "../components/ui";
import { Cover } from "../components/Cover";
import { CoverStack } from "../components/CoverStack";
import { seedLabel } from "../lib/seedLabel";
import { getCatalog, getMyStories } from "../lib/dataSource";
import { useChildren } from "../lib/childrenStore";
import { bandForAge, bandOf, type AgeBand } from "../lib/ageBands";
import { isScheduled, useAdminView } from "../lib/adminView";
import type { Catalog, StoryCard } from "../lib/types";

export default function Catalogue() {
  const { t } = useTranslation();
  const { currentChild } = useChildren();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  // Les histoires sur mesure de l'enfant courant : un parent qui ouvre le
  // Catalogue s'attend à y retrouver *toutes* les histoires, pas seulement
  // celles du catalogue commun. Même source que la liste de l'accueil.
  const [customStories, setCustomStories] = useState<StoryCard[]>([]);
  const [band, setBand] = useState<AgeBand>(() => (currentChild ? bandForAge(currentChild.age) : "all"));
  const adminView = useAdminView();
  const scheduledDateFormat = useMemo(
    () => new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", timeZone: "Europe/Paris" }),
    [],
  );

  useEffect(() => {
    void getCatalog(currentChild?.id, { adminView }).then(setCatalog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChild?.id, adminView]);

  useEffect(() => {
    if (!currentChild) {
      setCustomStories([]);
      return;
    }
    // Un échec ici ne doit pas empêcher d'afficher le catalogue commun : la
    // section reste simplement vide.
    getMyStories(currentChild.id).then(setCustomStories, () => setCustomStories([]));
  }, [currentChild?.id]);

  useEffect(() => {
    setBand(currentChild ? bandForAge(currentChild.age) : "all");
  }, [currentChild?.id]);

  const filteredFree = useMemo(() => {
    if (!catalog) return [];
    if (band === "all") return catalog.free;
    return catalog.free.filter((s) => bandOf(s) === band);
  }, [catalog, band]);

  // Les 2 premiers titres suffisent à faire deviner le contenu des packs
  // sans rallonger la carte ; l'ellipse ne s'affiche que s'il y en a
  // vraiment d'autres derrière.
  const packTeaserTitles = useMemo(() => {
    if (!catalog || catalog.packs.length === 0) return "";
    const names = catalog.packs.slice(0, 2).map((p) => p.title).join(", ");
    return catalog.packs.length > 2 ? `${names}…` : names;
  }, [catalog]);
  // The admin view unlocks every pack server-side, which says nothing about
  // the launch offer, so it never shows the "offered" wording.
  const allPacksUnlocked =
    !adminView && !!catalog && catalog.packs.length > 0 && catalog.packs.every((p) => p.unlocked);

  return (
    <PageLayout>
      <Container className="pt-8 flex flex-col gap-6">
        <Headline>{t("catalog.title")}</Headline>

        {!catalog ? (
          <MutedText>{t("common.loading")}</MutedText>
        ) : (
          <>
            {/* Teaser vers la section Packs plus bas : sans lui, un parent qui
                s'arrête aux histoires gratuites ne découvre les packs qu'en
                scrollant jusqu'au bout de la page. */}
            {catalog.packs.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  document.getElementById("packs")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="block w-full text-left"
              >
                <Card className="flex items-center gap-3">
                  <div className="flex shrink-0">
                    {catalog.packs.slice(0, 3).map((p, i) => (
                      <Cover
                        key={p.id}
                        svg={p.cover_svg}
                        title={p.title}
                        colorway={p.cover_colorway}
                        colorKey={p.slug}
                        className={`w-10 !border-2 !border-night ${i > 0 ? "-ml-3" : ""}`}
                      />
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold text-sm">{t("catalog.packsTeaserTitle")}</p>
                    <MutedText className="mt-0.5 truncate">
                      {t("catalog.packsTeaserSummary", { count: catalog.packs.length, titles: packTeaserTitles })}
                    </MutedText>
                    <p className="mt-1 text-[11px] font-heading font-semibold text-sun">
                      {allPacksUnlocked ? t("catalog.packsTeaserLaunchOffer") : t("catalog.plusBadge")}
                    </p>
                  </div>
                </Card>
              </button>
            )}

            {/* Pas de filtre d'âge ici : ces histoires ont été écrites pour cet
                enfant-là, la tranche d'âge n'a pas de sens. */}
            {currentChild && (
              <section>
                <h2 className="font-heading font-bold text-lg mb-3">
                  {t("catalog.customStories", { name: currentChild.first_name })}
                </h2>
                {customStories.length === 0 ? (
                  <Link to="/create">
                    <Card className="!bg-surface-raised !border-2 !border-coral/50 flex items-center gap-3">
                      <Feather className="text-coral shrink-0" size={28} />
                      <span className="font-heading font-semibold text-sm">
                        {t("home.writeForChild", { name: currentChild.first_name })}
                      </span>
                      <ChevronRight className="text-coral ml-auto shrink-0" size={20} aria-hidden />
                    </Card>
                  </Link>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {customStories.map((s) => (
                      <Link key={s.id} to={`/story/${s.id}`} className="flex flex-col gap-1.5">
                        <div className="relative">
                          <Cover
                            svg={s.cover_svg}
                            imageUrl={s.cover_image_url}
                            imageThumbUrl={s.cover_image_thumb_url}
                            title={s.title || t("home.untitled")}
                            colorway={s.cover_colorway}
                            colorKey={s.slug || s.id}
                            dimmed={s.status === "failed"}
                          />
                          {s.status === "generating" && (
                            <div className="absolute inset-0 flex items-center justify-center bg-night/70 rounded-[8px]">
                              <Loader2 className="animate-spin text-sun" size={24} />
                            </div>
                          )}
                        </div>
                        <p className="font-heading font-semibold text-xs leading-tight line-clamp-2">
                          {s.title || t("home.untitled")}
                        </p>
                        {s.status === "generating" ? (
                          <span className="text-[11px] text-violet-soft font-body">{t("home.generating")}</span>
                        ) : s.status === "failed" ? (
                          <span className="text-[11px] text-coral font-body">{t("home.failed")}</span>
                        ) : (
                          <SeedTag>{seedLabel(s.seed)}</SeedTag>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">{t("catalog.freeStories")}</h2>
              <div className="flex gap-2 overflow-x-auto pb-1 mb-3">
                <Pill active={band === "all"} onClick={() => setBand("all")}>
                  {t("catalog.bandAll")}
                </Pill>
                <Pill active={band === "3-5"} onClick={() => setBand("3-5")}>
                  {t("catalog.band35")}
                </Pill>
                <Pill active={band === "5-7"} onClick={() => setBand("5-7")}>
                  {t("catalog.band57")}
                </Pill>
                <Pill active={band === "8-10"} onClick={() => setBand("8-10")}>
                  {t("catalog.band810")}
                </Pill>
              </div>
              {filteredFree.length === 0 ? (
                <MutedText>{t("catalog.emptyForBand")}</MutedText>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredFree.map((s) => (
                    <Link key={s.id} to={`/story/${s.id}`} className="flex flex-col gap-1.5">
                      <div className="relative">
                        <Cover svg={s.cover_svg} imageUrl={s.cover_image_url} imageThumbUrl={s.cover_image_thumb_url} title={s.title} colorway={s.cover_colorway} colorKey={s.slug} />
                        {s.is_new && <NewBadge className="absolute top-1.5 left-1.5" />}
                        {adminView && isScheduled(s) && (
                          <ScheduledBadge
                            date={s.published_at ? scheduledDateFormat.format(new Date(s.published_at)) : null}
                            className="absolute top-1.5 left-1.5"
                          />
                        )}
                      </div>
                      <p className="font-heading font-semibold text-xs leading-tight line-clamp-2">{s.title}</p>
                      <SeedTag>{seedLabel(s.seed)}</SeedTag>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {catalog.packs.length > 0 && (
              <section id="packs">
                <h2 className="font-heading font-bold text-lg mb-3">{t("catalog.packs")}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {catalog.packs.map((p) => (
                    <Link key={p.id} to={`/pack/${p.id}`}>
                      <Card className="p-2 flex flex-col gap-2">
                        <CoverStack svg={p.cover_svg} imageUrl={p.cover_image_url} imageThumbUrl={p.cover_image_thumb_url} title={p.title} colorway={p.cover_colorway} colorKey={p.slug}>
                          {p.is_active === false && <HiddenPackBadge className="absolute top-1.5 left-1.5" />}
                        </CoverStack>
                        <p className="font-heading font-semibold text-xs leading-tight">{p.title}</p>
                        <div className="flex items-center gap-1 text-[11px] font-heading font-semibold text-violet-soft">
                          {p.unlocked ? (
                            <>
                              <LockOpen size={12} className="text-mint" />
                              {t("catalog.unlocked")}
                            </>
                          ) : (
                            <>
                              <Lock size={12} />
                              {t("catalog.plusBadge")}
                            </>
                          )}
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </Container>
    </PageLayout>
  );
}
